import { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    IconButton,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OTPForm from "../../Subscriptions/Pages/Login/Components/Otplogin";
import { sendOtpApi, verifyOtpApi } from "../../../api/otpApi";
// Uncomment this to enable 2FA verification for bank accounts
// const BANK_2FA_STORAGE_KEY = "bank_account_2fa_verified";
// const BANK_2FA_VALID_MS = 5 * 60 * 1000; // 5 minutes

export function isBankAccount2FAVerified(): boolean {
    // try {
    //     const stored = sessionStorage.getItem(BANK_2FA_STORAGE_KEY);
    //     if (!stored) return false;
    //     return Date.now() - parseInt(stored, 10) < BANK_2FA_VALID_MS;
    // } catch {
    //     return false;
    // }
    return false;
}

// export function markBankAccount2FAVerified(): void {
//     try {
//         sessionStorage.setItem(BANK_2FA_STORAGE_KEY, Date.now().toString());
//     } catch {
//         // ignore storage errors
//     }
// }

type Props = {
    open: boolean;
    phoneNumber: string; // digits only, no +91 prefix
    onClose: () => void;
    onVerified: () => void;
};

export default function BankAccount2FADialog({ open, phoneNumber, onClose, onVerified }: Props) {
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [otpFormReady, setOtpFormReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isOtpSubmitLoading, setIsOtpSubmitLoading] = useState(false);
    const [invalidOtpError, setInvalidOtpError] = useState(false);
    const [otpDeliveryMethod, setOtpDeliveryMethod] = useState<"whatsapp" | "sms" | null>(null);
    const [smsAttempted, setSmsAttempted] = useState(false);
    const [canResendSms, setCanResendSms] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);
    const [statusMessage, setStatusMessage] = useState("");

    // Auto-send OTP via WhatsApp when dialog opens
    useEffect(() => {
        if (open && !isOtpSent) {
            sendOtp("whatsapp");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            reset();
        }
    }, [open]);

    // SMS resend timer countdown
    useEffect(() => {
        if (resendTimer > 0) {
            const timerId = setInterval(() => setResendTimer((p) => p - 1), 1000);
            return () => clearInterval(timerId);
        } else if (smsAttempted) {
            setCanResendSms(true);
        }
    }, [resendTimer, smsAttempted]);

    const sendOtp = async (type: "whatsapp" | "sms") => {
        if (!phoneNumber) {
            setStatusMessage("Phone number not available. Please contact support.");
            return;
        }
        setIsLoading(true);
        setStatusMessage(`Sending OTP via ${type === "sms" ? "SMS" : "WhatsApp"}...`);
        try {
            const result = await sendOtpApi("+91" + phoneNumber, "bypassed", type);
            if (result.success) {
                setIsOtpSent(true);
                setOtpFormReady(true);
                setOtpDeliveryMethod(type);
                setStatusMessage("");
            } else {
                setStatusMessage(result.message || "Failed to send OTP. Please try again.");
            }
        } catch {
            setStatusMessage("Failed to send OTP. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendViaSMS = async () => {
        if (smsAttempted && !canResendSms) return;
        setSmsAttempted(true);
        setCanResendSms(false);
        setResendTimer(30);
        setInvalidOtpError(false);
        await sendOtp("sms");
    };

    const handleVerifyOtp = async (otp: string) => {
        setIsOtpSubmitLoading(true);
        setInvalidOtpError(false);
        setStatusMessage("Verifying OTP...");
        try {
            const result = await verifyOtpApi("+91" + phoneNumber, otp);
            if (result.success) {
                onClose();
                onVerified();
            } else {
                setInvalidOtpError(true);
                setStatusMessage(result.message || "Invalid OTP. Please try again.");
            }
        } catch {
            setInvalidOtpError(true);
            setStatusMessage("Verification failed. Please try again.");
        } finally {
            setIsOtpSubmitLoading(false);
        }
    };

    function reset() {
        setIsOtpSent(false);
        setOtpFormReady(false);
        setIsLoading(false);
        setIsOtpSubmitLoading(false);
        setInvalidOtpError(false);
        setOtpDeliveryMethod(null);
        setSmsAttempted(false);
        setCanResendSms(false);
        setResendTimer(0);
        setStatusMessage("");
    }

    function handleClose() {
        onClose();
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            sx={{
                "& .MuiPaper-root": {
                    borderRadius: "12px",
                },
            }}
        >
            <DialogTitle sx={{ pb: 0.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600} fontSize="1.1rem">
                        Verify Your Identity
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                    For security, please verify your identity before adding a bank account.
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                {isLoading && !isOtpSent ? (
                    <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
                        <CircularProgress size={32} />
                        <Typography color="text.secondary" fontSize="0.875rem">
                            {statusMessage || "Sending OTP..."}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <OTPForm
                            phoneNumber={phoneNumber}
                            onVerifyOTP={handleVerifyOtp}
                            setIsPhoneNumberSubmitted={() => {}}
                            setInvalidOtpError={setInvalidOtpError}
                            InvalidOtpError={invalidOtpError}
                            onSubmitPhoneNumber={() => sendOtp("whatsapp")}
                            otpFormReady={otpFormReady}
                            isOtpSubmitLoading={isOtpSubmitLoading}
                            handleSendViaSMS={handleSendViaSMS}
                            smsAttempted={smsAttempted}
                            canResendSms={canResendSms}
                            resendTimer={resendTimer}
                            isLoading={isLoading}
                            otpDeliveryMethod={otpDeliveryMethod}
                            hideWrongNumber={true}
                        />
                        {statusMessage && isOtpSent && (
                            <Typography
                                color={invalidOtpError ? "error" : "text.secondary"}
                                fontSize="0.75rem"
                                textAlign="center"
                                mt={1}
                            >
                                {statusMessage}
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
